import { useState } from "react";

import { useTranslation } from "react-i18next";
import styled from "styled-components";

import type { onPinExercise } from "routes/patients/views/Treatment/components/TreatmentEditor/ActivityCard/ActivityCard";

import { ContentContainer, ContentContainerHeader, ContentContainerHeaderLabel } from "../styles";
import type {
  HandleLevelChange,
  HandleSetsAndRepsChange,
  TimelineActivity,
  TimelineWeek,
  TreatmentEditorSettings,
} from "../types";

import { WeekRow } from "./WeekRow";

interface WeekTimelineProps {
  currentWeek: number;
  editorSettings: TreatmentEditorSettings;
  exercisesPerDay?: number;
  isDraggingFromLibrary: boolean;
  isMultiSelectMode: boolean;
  noExercisesError: boolean;
  selectedActivities: Set<string>;
  totalPinnedCount?: number;
  weeks: TimelineWeek[];
  weekWarnings: Map<number, string>;
  onActivityClick: (activity: TimelineActivity) => void;
  onCopyActivity: (activity: TimelineActivity) => void;
  onCopySelected: (weekNumber: number) => void;
  onDismissWarning: (weekNumber: number) => void;
  onLevelChange?: HandleLevelChange;
  onPinExercise?: onPinExercise;
  onPreview: (id: number, part: number) => void;
  onRemoveActivity: (activity: TimelineActivity, weekNumber: number) => void;
  onToggleExpanded: (weekNumber: number) => void;
  onToggleLevelLock?: HandleLevelChange;
  onSetsAndRepsChange?: HandleSetsAndRepsChange;
}

export const WeekTimeline: React.FC<WeekTimelineProps> = ({
  currentWeek,
  editorSettings,
  exercisesPerDay,
  isDraggingFromLibrary,
  isMultiSelectMode,
  noExercisesError,
  selectedActivities,
  totalPinnedCount,
  weeks,
  weekWarnings,
  onActivityClick,
  onCopyActivity,
  onCopySelected,
  onDismissWarning,
  onLevelChange,
  onPinExercise,
  onPreview,
  onRemoveActivity,
  onToggleExpanded,
  onToggleLevelLock,
  onSetsAndRepsChange,
}) => {
  const { t } = useTranslation();
  const [showPastWeeks, setShowPastWeeks] = useState(false);

  if (editorSettings.context === "library") {
    return (
      <Container>
        {weeks.map(week => (
          <WeekRow
            currentWeek={currentWeek}
            exercisesPerDay={exercisesPerDay}
            isCurrent={week.weekNumber === currentWeek}
            isDraggingFromLibrary={isDraggingFromLibrary}
            isMultiSelectMode={isMultiSelectMode}
            noExercisesError={noExercisesError}
            key={week.weekNumber}
            selectedActivities={selectedActivities}
            totalPinnedCount={totalPinnedCount}
            warning={weekWarnings.get(week.weekNumber)}
            week={week}
            onActivityClick={onActivityClick}
            onCopyActivity={onCopyActivity}
            onCopySelected={onCopySelected}
            onDismissWarning={onDismissWarning}
            onLevelChange={onLevelChange}
            onPinExercise={onPinExercise}
            onPreview={onPreview}
            onRemoveActivity={onRemoveActivity}
            onToggleExpanded={onToggleExpanded}
            onToggleLevelLock={onToggleLevelLock}
            onSetsAndRepsChange={onSetsAndRepsChange}
          />
        ))}
      </Container>
    );
  }

  const pastWeeks = weeks.filter(week => week.weekNumber < currentWeek);
  const futureWeeks = weeks.filter(week => week.weekNumber >= currentWeek);

  return (
    <Container>
      {pastWeeks.length > 0 && (
        <ContentContainer className="is-past">
          <ContentContainerHeader className="is-past" onClick={() => setShowPastWeeks(prevState => !prevState)}>
            <ContentContainerHeaderLabel>
              {showPastWeeks ? t("treatment.editor.hide_past_weeks") : t("treatment.editor.show_past_weeks")}
            </ContentContainerHeaderLabel>
          </ContentContainerHeader>

          {showPastWeeks && (
            <PastWeeksList>
              {pastWeeks.map(week => (
                <WeekRow
                  currentWeek={currentWeek}
                  exercisesPerDay={exercisesPerDay}
                  isCurrent={week.weekNumber === currentWeek}
                  isDraggingFromLibrary={isDraggingFromLibrary}
                  isMultiSelectMode={isMultiSelectMode}
                  isPastWeek
                  noExercisesError={noExercisesError}
                  key={week.weekNumber}
                  selectedActivities={selectedActivities}
                  totalPinnedCount={totalPinnedCount}
                  warning={weekWarnings.get(week.weekNumber)}
                  week={week}
                  onActivityClick={onActivityClick}
                  onCopyActivity={onCopyActivity}
                  onCopySelected={onCopySelected}
                  onDismissWarning={onDismissWarning}
                  onLevelChange={onLevelChange}
                  onPinExercise={onPinExercise}
                  onPreview={onPreview}
                  onRemoveActivity={onRemoveActivity}
                  onToggleExpanded={onToggleExpanded}
                  onToggleLevelLock={onToggleLevelLock}
                  onSetsAndRepsChange={onSetsAndRepsChange}
                />
              ))}
            </PastWeeksList>
          )}
        </ContentContainer>
      )}

      {futureWeeks.map(week => (
        <WeekRow
          currentWeek={currentWeek}
          exercisesPerDay={exercisesPerDay}
          isCurrent={week.weekNumber === currentWeek}
          isDraggingFromLibrary={isDraggingFromLibrary}
          isMultiSelectMode={isMultiSelectMode}
          noExercisesError={noExercisesError}
          key={week.weekNumber}
          selectedActivities={selectedActivities}
          totalPinnedCount={totalPinnedCount}
          warning={weekWarnings.get(week.weekNumber)}
          week={week}
          onActivityClick={onActivityClick}
          onCopyActivity={onCopyActivity}
          onCopySelected={onCopySelected}
          onDismissWarning={onDismissWarning}
          onLevelChange={onLevelChange}
          onPinExercise={onPinExercise}
          onPreview={onPreview}
          onRemoveActivity={onRemoveActivity}
          onToggleExpanded={onToggleExpanded}
          onToggleLevelLock={onToggleLevelLock}
          onSetsAndRepsChange={onSetsAndRepsChange}
        />
      ))}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  margin: 0 auto;
`;

const PastWeeksList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
`;
